// Helpers for writing through the typed Supabase client.

/**
 * Strip undefined-valued keys from a write payload.
 *
 * The Supabase-generated Insert/Update types model nullable columns as
 * `?: T | null` — never `?: T | undefined`. Zod `.optional()`, however,
 * infers `?: T | undefined`. Under `exactOptionalPropertyTypes` those are
 * incompatible, so a Zod-validated input object cannot be passed straight
 * to `.insert()` / `.update()`.
 *
 * At runtime an `undefined`-valued key is already dropped by JSON
 * serialization before it reaches PostgREST, so removing it changes
 * nothing — this just makes the type honest. The return type drops
 * `undefined` from every property's value type while preserving optionality.
 */
export function stripUndefined<T extends object>(
  obj: T,
): {
  [K in keyof T]: Exclude<T[K], undefined>;
} {
  const entries = Object.entries(obj as Record<string, unknown>).filter(
    ([, value]) => value !== undefined,
  );
  return Object.fromEntries(entries) as {
    [K in keyof T]: Exclude<T[K], undefined>;
  };
}
